import {
  AnalyzeExperienceFrontendTinybirdRequestBodyType,
  AnalyzeTinybirdResourceResponseBodyType,
  ExperienceFe,
} from "@intelligems/ig-types";
import { GraphConfig } from "@intelligems/ig-types";
import { TransformedDataset } from "@src/api/analytics/types";
import { useBuildTinybirdPayload } from "@src/api/analytics/use-build-tinybird-payload";
import {
  normalizeTinybirdAnalyticsResponse,
  transformMetricsConfig,
} from "@src/api/analytics/utils";
import { analyticsApi } from "@src/api/api";
import { dashboardKeys } from "@src/api/queryKeys";
import { useSelector } from "@src/store";
import { getExperienceAnalyticsFilters } from "@src/store/slices/dashboardFilters";
import { useQueries, UseQueryResult } from "@tanstack/react-query";

const fetchShareAnalyticsDataset = async (
  data: AnalyzeExperienceFrontendTinybirdRequestBodyType
): Promise<AnalyzeTinybirdResourceResponseBodyType> => {
  const resp = await analyticsApi.post<
    AnalyzeTinybirdResourceResponseBodyType,
    AnalyzeExperienceFrontendTinybirdRequestBodyType
  >(`/v2/analytics/analyze-resource-tinybird`, data);

  return normalizeTinybirdAnalyticsResponse(resp.data);
};

/**
 * Fetches analytics data for shared experience views.
 *
 * Transforms graph configs into dataset groups and fetches analytics
 * for each dataset in parallel using React Query.
 *
 * @param experienceId - The experience ID to fetch analytics for
 * @param experienceFe - Transformed experience (ExperienceFe type)
 * @param graphConfigs - Array of graph configurations defining which metrics to fetch
 * @returns Array of React Query results, one for each dataset group
 */
export const useShareAnalytics = (
  experienceId: string | undefined,
  experienceFe: ExperienceFe | undefined,
  graphConfigs: GraphConfig[]
): UseQueryResult<AnalyzeTinybirdResourceResponseBodyType>[] => {
  const buildTinybirdPayload = useBuildTinybirdPayload();
  const dashboardFilters = useSelector(getExperienceAnalyticsFilters);

  // Transform graph configs to dataset groups (combines metrics by datasetId)
  const transformedDatasets: TransformedDataset[] =
    transformMetricsConfig(graphConfigs);

  return useQueries({
    queries: transformedDatasets.map((datasetGroup) => {
      // Guard: Don't build payload if experience is missing
      if (!experienceFe) {
        return {
          queryKey: ["share-analytics-disabled", datasetGroup.datasetId],
          queryFn: async () => {
            throw new Error("Experience required for analytics query");
          },
          enabled: false,
        };
      }

      // Build payload with Redux filters (like regular analytics)
      const data = buildTinybirdPayload.build(
        {
          experience: experienceFe,
          graphConfigs,
          overviewDataLoaded: true,
        },
        datasetGroup,
        "experiences"
      );

      return {
        queryKey: [dashboardKeys.selectedTinybirdExperiment(data)],
        queryFn: async () => fetchShareAnalyticsDataset(data),
        enabled:
          Boolean(experienceId && experienceFe) &&
          dashboardFilters.hiddenEndTime !== null,
        retry: false,
        // Share analytics - optimize caching but allow filter-triggered refetches
        staleTime: 5 * 60 * 1000, // 5 minutes
        gcTime: 10 * 60 * 1000, // 10 minutes
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
      };
    }),
  });
};
